import React from 'react'
import { useDispatch } from 'react-redux'
import { LAYOUT_SECTION } from '@adalo/constants'
import SingleNumberControl from '../../../Inspect/SingleNumberControl'
import getDeviceObject from '../../../../../utils/getDeviceObject'
import {
  enableDeviceSpecificLayout,
  moveElement,
  resizeElement,
} from '../../../../../ducks/editor/instructions'
import { runInstructions } from '../../../../../ducks/editor/objects'
import { EditorObject } from '../../../../../utils/responsiveTypes'
import DeviceType from '../../../../../ducks/editor/types/DeviceType'
import { getContainerFromSection } from '../../../../../utils/layoutSections'
import { Instruction } from '../../../../../ducks/editor/instructions/applyInstructions'

interface AdvancedLayoutProps {
  object: EditorObject
  device: DeviceType
}

export function LayoutAdvancedSettings(
  props: AdvancedLayoutProps
): JSX.Element {
  const { object, device } = props
  const { width, height, x, y, id, type, initialDevice } = getDeviceObject(
    object,
    device
  )
  const isLayoutSection = type === LAYOUT_SECTION
  const dispatch = useDispatch()

  let verticalPadding = 0
  let horizontalPadding = 0

  if (isLayoutSection && object.children) {
    const container = getContainerFromSection(object)
    const { x: childX, y: childY } = getDeviceObject(container, device)
    verticalPadding = childY - y
    horizontalPadding = childX - x
  }

  const handleChange = ({
    width: newWidth = width,
    height: newHeight = height,
  }: {
    width?: number
    height?: number
  }) => {
    if (newWidth < 0 || newHeight < 0) {
      return
    }

    const horizontalCenter = x + width / 2
    const newX = isLayoutSection ? x : horizontalCenter - newWidth / 2

    const verticalCenter = y + height / 2
    const newY = isLayoutSection ? y : verticalCenter - newHeight / 2

    const instructions: Instruction[] = []

    if (initialDevice && initialDevice !== device && !object[device]) {
      instructions.push(enableDeviceSpecificLayout(id, device))
    }

    instructions.push(resizeElement(id, newWidth, newHeight))
    instructions.push(moveElement(id, newX, newY))

    dispatch(runInstructions(instructions))
  }

  const handlePaddingChange = ({
    horizontalPadding: newHorizontalPadding = horizontalPadding,
    verticalPadding: newVerticalPadding = verticalPadding,
  }: {
    horizontalPadding?: number
    verticalPadding?: number
  }) => {
    if (
      newHorizontalPadding < 0 ||
      newVerticalPadding < 0 ||
      newHorizontalPadding > width / 2 ||
      newVerticalPadding > height / 2
    ) {
      return
    }

    const container = getContainerFromSection(object)
    const { id: childId, initialDevice: childInitialDevice } = container

    const newX = x + newHorizontalPadding
    const newY = y + newVerticalPadding
    const newWidth = width - 2 * newHorizontalPadding
    const newHeight = height - 2 * newVerticalPadding

    const instructions: Instruction[] = []

    if (
      childInitialDevice &&
      childInitialDevice !== device &&
      !container[device]
    ) {
      instructions.push(enableDeviceSpecificLayout(childId, device))
    }

    instructions.push(resizeElement(childId, newWidth, newHeight))
    instructions.push(moveElement(childId, newX, newY))

    dispatch(runInstructions(instructions))
  }

  return (
    <div className="form-inspect-field-wrapper">
      <div className="advanced-styles-row">
        <SingleNumberControl
          title="Width"
          name="width"
          value={Math.round(width)}
          onChange={handleChange}
        />
        <SingleNumberControl
          title="Height"
          name="height"
          value={Math.round(height)}
          onChange={handleChange}
        />
      </div>
      {isLayoutSection && (
        <div className="advanced-styles-row">
          <SingleNumberControl
            title="Left & Right Space"
            name="horizontalPadding"
            value={Math.round(horizontalPadding)}
            onChange={handlePaddingChange}
          />
          <SingleNumberControl
            title="Top & Bottom Space"
            name="verticalPadding"
            value={Math.round(verticalPadding)}
            onChange={handlePaddingChange}
          />
        </div>
      )}
    </div>
  )
}
